import {
  Case,
  CaseAdmin,
  LISCase,
  Specimen,
  SpecimenAdmin,
  SpecimenProtocolOverride,
} from "pwapi";

import { CaseDetailState } from "reducers/caseDetailReducer";

export const use_pw_source_attributes = (
  specimen: Specimen | Exclude<Case["specimens"], undefined>[0],
  _specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  // TODO: enable this _after_ a transitionary period
  // if (specimenCase?.source === Case.source.PW) return true;
  if (!specimen.source && specimen.type && specimen.site && specimen.procedure)
    return true;
  return false;
};

export const has_source_attributes = (
  specimen: Specimen,
  specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  return !!(use_pw_source_attributes(specimen, specimenCase)
    ? specimen.type && specimen.site && specimen.procedure
    : specimen.source);
};

export const specimen_source = (
  specimen: Specimen,
  specimenCase?: CaseDetailState | CaseAdmin | Case
) => {
  if (use_pw_source_attributes(specimen, specimenCase) && specimen.procedure)
    return [
      specimen.type,
      specimen.site?.toUpperCase(),
      procedureEnumToLabel(specimen.procedure),
    ].join(", ");
  return specimen.source;
};

export const _compareSpecimens = (a: Specimen, b: Specimen): number =>
  a.name.localeCompare(b.name, "en", {
    numeric: true,
    sensitivity: "base",
  });

// DO NOT USE THIS FUNCTION DIRECTLY
//
// sortSpecimens() is a pure function with no memoization or caching built in.
// Instead, use functions from selectors/selectSortedCaseDetail.ts or hooks/useSortedCase.
// It is preferable to use the selectors when possible.
export const sortSpecimens = (specimens: Specimen[] = []): Specimen[] =>
  [...specimens].sort(_compareSpecimens);

const procedureLabels = {
  c: "CURRETTINGS",
  e: "EXCISION",
  pe: "PUNCH EXCISION",
  ib: "INCISIONAL BIOPSY",
  nc: "NAIL CLIPPING",
  pb: "PUNCH BIOPSY",
  sb: "SHAVE BIOPSY",
  sbd: "SHAVE BIOPSY AND DESTRUCTION",
  sr: "SHAVE REMOVAL",
  srd: "SHAVE REMOVAL AND DESTRUCTION",
  u: "UNKNOWN",
  snb: "SNIP BIOPSY",
  gr: "GRADLE REMOVAL",
};
export const procedureEnumToLabel = (
  shortEnum: Specimen.procedure | LISCase["specimens"][0]["procedure"]
) => {
  return (shortEnum && procedureLabels[shortEnum]) || shortEnum;
};

export const protocolLabels: {
  [key in SpecimenProtocolOverride.protocol]: string;
} = {
  nail: "Nail",
  dif: "DIF",
  alopecia: "Alopecia",
  whole_specimen: "Whole Specimen",
};

type ProtocolStatus = {
  active: boolean;
  reason: string;
};

type LISSpecimen = LISCase["specimens"][0];

// Protocol logic here is duplicated from the backend and is for display purposes only.
// The logic here should only be changed when copying changes from the backend.
// Backend src: /packages/backend/apps/histoslide/protocols.py

export const getProtocolStatus = (
  specimen: Specimen | LISSpecimen | SpecimenAdmin,
  protocol: SpecimenProtocolOverride.protocol,
  ignoreOverrides?: boolean
): ProtocolStatus => {
  const overrides = (specimen as SpecimenAdmin).protocol_overrides || [];
  if (
    !ignoreOverrides &&
    overrides.find((o) => o.protocol === protocol) !== undefined
  )
    return {
      active: !!overrides.find((o) => o.protocol === protocol)?.active,
      reason: "Manual override",
    };

  if (protocol === "nail") {
    const hasNailType = specimen.type === Specimen.type.NAIL;
    const hasNailCuttingProcedure =
      specimen.procedure === Specimen.procedure.NC;
    const nail = hasNailType || hasNailCuttingProcedure;

    return {
      active: nail,
      reason: !nail
        ? "Specimen does not have Type = Nail or Procedure = Nail Cutting"
        : `Specimen has ${[
            hasNailType ? "Type = Nail" : null,
            hasNailCuttingProcedure ? "Procedure = Nail Cutting" : null,
          ]
            .filter((v) => !!v)
            .join(" and ")}`,
    };
  }

  if (protocol === "dif") {
    const dif = !!(specimen.clinical_history || "")
      .toLowerCase()
      .match(/\bdif\b/);

    return {
      active: dif,
      reason: !dif
        ? "Specimen does not have DIF keyword in its Clinical History"
        : "Specimen has DIF keyword in its Clinical History",
    };
  }

  if (protocol === "alopecia") {
    const hasAlopeciaKeyword =
      !!specimen.clinical_history &&
      [
        // "alopecia areata", // This is redundant after adding "alopecia"
        "aplasia cutis congenita",
        // "androgenetic alopecia", // This is redundant after adding "alopecia"
        // "central centrifugal cicatricial alopecia", // This is redundant after adding "alopecia"
        "dissecting cellulitis",
        "folliculitis decalvans",
        // "frontal fibrosing alopecia", // This is redundant after adding "alopecia"
        "female pattern baldness",
        "loose anagen syndrome",
        "lichen planopilaris",
        "telogen effluvium",
        "trichotillomania",
        "hair loss",
        "alopecia", // This may be too general but all effects from the ALOPECIA Protocol are toggleable so it's okay for now
      ].some(
        (keyword) =>
          (specimen.clinical_history || "").toLowerCase().indexOf(keyword) > -1
      );
    // TODO: Implement logic for checking for Alopecia tag from ModMed
    const hasAlopeciaModmed = false;
    const alopecia = hasAlopeciaKeyword || hasAlopeciaModmed;

    return {
      active: alopecia,
      reason: !alopecia
        ? "Specimen does not have Alopecia keywords in its Clinical History"
        : "Specimen has Alopecia keywords in its Clinical History",
    };
  }

  if (protocol === "whole_specimen") {
    const wholeSpecimen =
      specimen.grossing_token_selections?.grossed &&
      !specimen.grossing_token_selections?.grossed?.value;

    return {
      active: wholeSpecimen,
      reason: wholeSpecimen
        ? "Specimen was grossed without keywords 'bisected', 'trisected', or 'serially sectioned'"
        : !specimen.grossing_token_selections?.grossed
        ? "Specimen has not been grossed"
        : `Specimen was grossed with keyword '${specimen.grossing_token_selections?.biopsy?.value}'`,
    };
  }

  return {
    active: false,
    reason: "Not yet implemented",
  };
};

export const getProtocolStatuses = (
  specimen: Specimen | LISSpecimen,
  ignoreOverrides?: boolean
): { [key in SpecimenProtocolOverride.protocol]: ProtocolStatus } => ({
  alopecia: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.ALOPECIA,
    ignoreOverrides
  ),
  dif: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.DIF,
    ignoreOverrides
  ),
  nail: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.NAIL,
    ignoreOverrides
  ),
  whole_specimen: getProtocolStatus(
    specimen,
    SpecimenProtocolOverride.protocol.WHOLE_SPECIMEN,
    ignoreOverrides
  ),
});
